import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Calendar, TrendingUp, Baby, ArrowLeft, Sparkles, Clock } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Activities = () => {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [activities, setActivities] = useState([]);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activityForm, setActivityForm] = useState({
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchChildAndActivities();
  }, [childId]);

  const fetchChildAndActivities = async () => {
    try {
      // Fetch all children to find the current one
      const childrenResponse = await axios.get(`${API_BASE}/children`);
      const currentChild = childrenResponse.data.find(c => c.id === childId);
      setChild(currentChild);

      // Fetch activities for this child
      const activitiesResponse = await axios.get(`${API_BASE}/activities/${childId}`);
      setActivities(activitiesResponse.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.description.trim()) {
      toast.error('Please enter an activity description');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/activities`, {
        child_id: childId,
        description: activityForm.description,
        date: new Date(activityForm.date).toISOString()
      });
      
      toast.success('Activity added successfully! AI analysis in progress...');
      setShowAddActivity(false);
      setActivityForm({ description: '', date: new Date().toISOString().split('T')[0] });
      
      // Refresh activities
      setTimeout(() => {
        fetchChildAndActivities();
      }, 1000); // Small delay to allow AI processing
    } catch (error) {
      toast.error('Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} months`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years}y ${months}m` : `${years} years`;
    }
  };

  const getMilestoneColor = (category) => {
    const colors = {
      'Motor': 'bg-blue-100 text-blue-700 border-blue-200',
      'Cognitive': 'bg-purple-100 text-purple-700 border-purple-200',
      'Language': 'bg-green-100 text-green-700 border-green-200',
      'Social': 'bg-pink-100 text-pink-700 border-pink-200',
      'Emotional': 'bg-amber-100 text-amber-700 border-amber-200',
      'Physical': 'bg-red-100 text-red-700 border-red-200',
      'General Development': 'bg-slate-100 text-slate-700 border-slate-200'
    };
    
    // Find matching category (case insensitive, partial match)
    const matchedCategory = Object.keys(colors).find(key => 
      category && category.toLowerCase().includes(key.toLowerCase())
    );
    
    return colors[matchedCategory] || colors['General Development'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading activities...</p>
        </div>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Child not found</p>
          <Link to="/dashboard">
            <Button className="gradient-emerald text-white">Go to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="btn-hover">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl lg:text-4xl font-bold font-display bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              {child.name}'s Activities
            </h1>
            <p className="text-slate-600 mt-1">
              {calculateAge(child.birth_date)} • {child.gender} • {activities.length} activities recorded
            </p>
          </div>
        </div>

        {/* Child Info Card */}
        <Card className="glass border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                <Baby className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-800">{child.name}</h2>
                <p className="text-slate-600">Born {new Date(child.birth_date).toLocaleDateString()}</p>
              </div>
              <div className="flex space-x-3">
                <Link to={`/reports/${child.id}`}>
                  <Button className="btn-hover gradient-teal text-white" data-testid="view-reports-button">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    View Reports
                  </Button>
                </Link>
                <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
                  <DialogTrigger asChild>
                    <Button className="btn-hover gradient-emerald text-white" data-testid="add-activity-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Activity
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Record New Activity</DialogTitle>
                      <DialogDescription>
                        Describe what {child.name} did today. Our AI will analyze the developmental significance.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddActivity} className="space-y-4" data-testid="add-activity-form">
                      <div>
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          type="date"
                          value={activityForm.date}
                          onChange={(e) => setActivityForm({...activityForm, date: e.target.value})}
                          className="form-input"
                          data-testid="activity-date-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description">What did {child.name} do?</Label>
                        <Textarea
                          id="description"
                          placeholder="e.g., Took first steps without support, said 'mama' clearly, built a tower with 3 blocks..."
                          value={activityForm.description}
                          onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                          rows={4}
                          className="form-input"
                          data-testid="activity-description-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={submitting}
                        className="w-full gradient-emerald text-white"
                        data-testid="save-activity-button"
                      >
                        {submitting ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Analyzing with AI...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            Record Activity
                          </div>
                        )}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activities Timeline */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="font-display text-2xl text-slate-800 flex items-center space-x-2">
              <Calendar className="w-6 h-6 text-emerald-600" />
              <span>Development Timeline</span>
            </CardTitle>
            <CardDescription>
              A chronological record of {child.name}'s growth and milestones
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activities.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No activities recorded yet</p>
                <Button 
                  onClick={() => setShowAddActivity(true)}
                  className="btn-hover gradient-emerald text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Record First Activity
                </Button>
              </div>
            ) : (
              <div className="timeline" data-testid="activities-timeline">
                {activities.map((activity, index) => (
                  <div key={activity.id} className="timeline-item" data-testid={`activity-${activity.id}`}>
                    <div className="timeline-dot"></div>
                    <Card className="bg-white/70 border border-emerald-200/50 card-hover">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-slate-500" />
                            <span className="text-sm font-medium text-slate-600">
                              {new Date(activity.date).toLocaleDateString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          {activity.milestone_category && (
                            <span 
                              className={`px-3 py-1 rounded-full text-xs font-medium border ${getMilestoneColor(activity.milestone_category)}`}
                              data-testid={`milestone-badge-${activity.id}`}
                            >
                              {activity.milestone_category}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="text-lg font-semibold text-slate-800 mb-3">
                          {activity.description}
                        </h3>
                        
                        {activity.ai_analysis && activity.ai_analysis !== 'AI analysis unavailable' && (
                          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mt-4">
                            <div className="flex items-start space-x-2">
                              <Sparkles className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-emerald-800 mb-1">AI Insight:</p>
                                <p className="text-sm text-emerald-700 leading-relaxed">
                                  {activity.ai_analysis}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                          <div className="flex items-center space-x-2 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            <span>Recorded {new Date(activity.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Statistics */}
        {activities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="glass border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-emerald-600 mb-2">{activities.length}</div>
                <p className="text-slate-600">Total Activities</p>
              </CardContent>
            </Card>
            
            <Card className="glass border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-teal-600 mb-2">
                  {new Set(activities.map(a => a.milestone_category).filter(Boolean)).size}
                </div>
                <p className="text-slate-600">Milestone Categories</p>
              </CardContent>
            </Card>
            
            <Card className="glass border-0">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-cyan-600 mb-2">
                  {activities.filter(a => a.ai_analysis && a.ai_analysis !== 'AI analysis unavailable').length}
                </div>
                <p className="text-slate-600">AI Analyzed</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;
